import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import {
  addGateRule,
  getMailConfig,
  getMailUsage,
  listGateRules,
  saveMailConfig,
  setGateRuleActive,
} from '../../services/mailIntelApi';
import {
  Btn,
  EmptyNote,
  ErrorBanner,
  SectionLabel,
  Spinner,
  humanise,
} from './shared';

const errText = (e) => String(e?.message || e);
const isForbidden = (e) => /403|forbidden|permission/i.test(errText(e));

const INPUT =
  'w-full rounded-lg border border-zinc-700 bg-zinc-900 px-2 py-1 text-[13px] text-zinc-100 outline-none focus:border-[#5652CF] disabled:opacity-60';

const ROLES = ['pm', 'ba', 'tl', 'consultant', 'associate'];
const WEIGHT = { 1: 'L', 2: 'M', 3: 'H' };
const SWITCH_LABEL = {
  classify: 'Relevance classifier (GLM)',
  thread_type: 'Thread type',
  annotate: 'Mail annotation',
  extract: 'Item extraction',
  summary: 'Thread summaries',
  drafting: 'Reply drafting',
  chat: 'Mail in chat',
};

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/** Overrides = what differs from the shipped defaults (the backend stores only these). */
export const diffFromDefaults = (values, defaults) => {
  const out = {};
  const weights = {};
  Object.entries(values.role_weights).forEach(([role, dims]) => {
    const changed = Object.fromEntries(
      Object.entries(dims).filter(
        ([dim, w]) => defaults.role_weights[role][dim] !== w,
      ),
    );
    if (Object.keys(changed).length) weights[role] = changed;
  });
  if (Object.keys(weights).length) out.role_weights = weights;
  ['bands', 'latency_hours', 'sla_hours', 'switches'].forEach((section) => {
    const changed = Object.fromEntries(
      Object.entries(values[section]).filter(
        ([k, v]) => !same(v, defaults[section][k]),
      ),
    );
    if (Object.keys(changed).length) out[section] = changed;
  });
  const tracked = [...values.tracked_types].sort();
  if (!same(tracked, [...defaults.tracked_types].sort())) {
    out.tracked_types = tracked;
  }
  const featureRoles = {};
  Object.entries(values.feature_roles || {}).forEach(([feature, roles]) => {
    const now = [...roles].sort();
    if (!same(now, [...(defaults.feature_roles?.[feature] || [])].sort())) {
      featureRoles[feature] = now;
    }
  });
  if (Object.keys(featureRoles).length) out.feature_roles = featureRoles;
  return out;
};

const NumberField = ({ label, value, onChange, step = 1, disabled }) => (
  <label className="text-[11px] text-zinc-400">
    {label}
    <input
      aria-label={label}
      type="number"
      step={step}
      min="0"
      className={INPUT}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(Number(e.target.value))}
    />
  </label>
);

// ---- triage settings ------------------------------------------------------

export const TriageSettingsSection = ({ accessToken }) => {
  const [data, setData] = useState(null);
  const [values, setValues] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await getMailConfig(accessToken);
      setData(res.data);
      setValues(res.data.values);
      setError(null);
    } catch (e) {
      setError(
        isForbidden(e)
          ? 'You need mail settings access to see these.'
          : errText(e),
      );
    }
  }, [accessToken]);

  useEffect(() => {
    load();
  }, [load]);

  if (error) return <ErrorBanner>{error}</ErrorBanner>;
  if (!values) return <Spinner />;

  const locked = !data.can_edit || saving;
  const set = (section, key, value) =>
    setValues({ ...values, [section]: { ...values[section], [key]: value } });
  const setWeight = (role, dim, w) =>
    setValues({
      ...values,
      role_weights: {
        ...values.role_weights,
        [role]: { ...values.role_weights[role], [dim]: w },
      },
    });
  const setLatency = (kind, i, v) => {
    const pair = [...values.latency_hours[kind]];
    pair[i] = v;
    set('latency_hours', kind, pair);
  };
  const toggleTracked = (type) =>
    setValues({
      ...values,
      tracked_types: values.tracked_types.includes(type)
        ? values.tracked_types.filter((t) => t !== type)
        : [...values.tracked_types, type],
    });
  const toggleFeatureRole = (feature, role) => {
    const roles = values.feature_roles[feature];
    set(
      'feature_roles',
      feature,
      roles.includes(role) ? roles.filter((r) => r !== role) : [...roles, role],
    );
  };
  const dirty = !same(values, data.values);

  const save = async () => {
    setSaving(true);
    try {
      const res = await saveMailConfig(
        accessToken,
        diffFromDefaults(values, data.defaults),
        data.version,
      );
      setData({ ...data, ...res.data });
      setValues(res.data.values);
      toast.success(
        `Saved (version ${res.data.version}). Live within a minute.`,
      );
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-[13px] text-zinc-400">
        How the Mail tab ranks threads and which stages run. Changes apply
        without a deploy, within a minute. Drafts and summaries record the
        version used (now version {data.version}).
      </p>

      <section>
        <SectionLabel>Role weights (L / M / H)</SectionLabel>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px] text-zinc-300">
            <thead>
              <tr>
                <th className="py-1 text-left font-medium">Dimension</th>
                {ROLES.map((r) => (
                  <th key={r} className="px-1 font-medium uppercase">
                    {r}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.dimensions.map((dim) => (
                <tr key={dim}>
                  <td className="py-0.5">{humanise(dim)}</td>
                  {ROLES.map((role) => (
                    <td key={role} className="px-1">
                      <select
                        aria-label={`${role} ${dim}`}
                        className={INPUT}
                        value={values.role_weights[role][dim]}
                        disabled={locked}
                        onChange={(e) =>
                          setWeight(role, dim, Number(e.target.value))
                        }
                      >
                        {[1, 2, 3].map((w) => (
                          <option key={w} value={w}>
                            {WEIGHT[w]}
                          </option>
                        ))}
                      </select>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <SectionLabel>Bands (lowest score)</SectionLabel>
        <div className="grid grid-cols-3 gap-2">
          {['critical', 'high', 'normal'].map((b) => (
            <NumberField
              key={b}
              label={humanise(b)}
              step={0.05}
              value={values.bands[b]}
              disabled={locked}
              onChange={(v) => set('bands', b, v)}
            />
          ))}
        </div>
      </section>

      <section>
        <SectionLabel>Reply windows (working hours)</SectionLabel>
        <div className="grid grid-cols-2 gap-2">
          {['external', 'internal'].map((kind) =>
            ['due', 'overdue'].map((step, i) => (
              <NumberField
                key={`${kind}-${step}`}
                label={`${humanise(kind)}: ${step} after`}
                value={values.latency_hours[kind][i]}
                disabled={locked}
                onChange={(v) => setLatency(kind, i, v)}
              />
            )),
          )}
          <NumberField
            label="Item due: imminent within (h)"
            value={values.sla_hours.imminent}
            disabled={locked}
            onChange={(v) => set('sla_hours', 'imminent', v)}
          />
          <NumberField
            label="Item due: approaching within (h)"
            value={values.sla_hours.approaching}
            disabled={locked}
            onChange={(v) => set('sla_hours', 'approaching', v)}
          />
        </div>
      </section>

      <section>
        <SectionLabel>Stages</SectionLabel>
        <div className="grid grid-cols-2 gap-1">
          {data.switches.map((s) => (
            <label key={s} className="flex items-center gap-2 text-[12px]">
              <input
                type="checkbox"
                checked={values.switches[s]}
                disabled={locked}
                onChange={(e) => set('switches', s, e.target.checked)}
              />
              {SWITCH_LABEL[s] || humanise(s)}
            </label>
          ))}
        </div>
      </section>

      <section>
        <SectionLabel>Tracked thread types</SectionLabel>
        <div className="grid grid-cols-2 gap-1">
          {data.defaults.tracked_types.map((t) => (
            <label key={t} className="flex items-center gap-2 text-[12px]">
              <input
                type="checkbox"
                checked={values.tracked_types.includes(t)}
                disabled={locked}
                onChange={() => toggleTracked(t)}
              />
              {humanise(t)}
            </label>
          ))}
        </div>
      </section>

      {values.feature_roles && (
        <section>
          <SectionLabel>Who can use (pilot by role)</SectionLabel>
          <table className="w-full text-[12px] text-zinc-300">
            <thead>
              <tr>
                <th className="py-1 text-left font-medium">Feature</th>
                {ROLES.map((r) => (
                  <th key={r} className="px-1 font-medium uppercase">
                    {r}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.keys(values.feature_roles).map((feature) => (
                <tr key={feature}>
                  <td className="py-0.5">{SWITCH_LABEL[feature] || feature}</td>
                  {ROLES.map((role) => (
                    <td key={role} className="px-1 text-center">
                      <input
                        type="checkbox"
                        aria-label={`${feature} for ${role}`}
                        checked={values.feature_roles[feature].includes(role)}
                        disabled={locked}
                        onChange={() => toggleFeatureRole(feature, role)}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {data.can_edit && (
        <div className="flex gap-2">
          <Btn variant="primary" onClick={save} disabled={!dirty || saving}>
            {saving ? 'Saving…' : 'Save settings'}
          </Btn>
          <Btn
            variant="ghost"
            onClick={() => setValues(data.values)}
            disabled={!dirty || saving}
          >
            Undo changes
          </Btn>
        </div>
      )}
    </div>
  );
};

// ---- gate rules -------------------------------------------------------------

const EMPTY_RULE = {
  kind: 'domain_allow',
  value: '',
  reason: '',
  scope: 'project',
};

export const GateRulesSection = ({ accessToken }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(EMPTY_RULE);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await listGateRules(accessToken);
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(
        isForbidden(e)
          ? 'You need mail settings access to see the gate rules.'
          : errText(e),
      );
    }
  }, [accessToken]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (action, done) => {
    setBusy(true);
    try {
      await action();
      toast.success(done);
      await load();
      return true;
    } catch (e) {
      toast.error(errText(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  if (error) return <ErrorBanner>{error}</ErrorBanner>;
  if (!data) return <Spinner />;

  return (
    <div className="flex flex-col gap-4">
      <p className="text-[13px] text-zinc-400">
        Fixed rules the relevance gate checks before any model call. Known
        client domains help include mail; blocks drop it with no body kept.
        Rules are switched off, never deleted. Changes apply within 10 minutes.
      </p>
      {data.can_edit && (
        <div className="grid grid-cols-2 gap-2 rounded-xl border border-zinc-700/60 bg-zinc-800/30 p-3">
          <label className="text-[11px] text-zinc-400">
            Rule
            <select
              aria-label="Rule kind"
              className={INPUT}
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value })}
            >
              {data.kinds.map((k) => (
                <option key={k} value={k}>
                  {humanise(k)}
                </option>
              ))}
            </select>
          </label>
          <label className="text-[11px] text-zinc-400">
            Applies to
            <select
              aria-label="Rule scope"
              className={INPUT}
              value={form.scope}
              onChange={(e) => setForm({ ...form, scope: e.target.value })}
            >
              <option value="project">This project</option>
              <option value="platform">Every project</option>
            </select>
          </label>
          <label className="text-[11px] text-zinc-400">
            Value
            <input
              aria-label="Rule value"
              className={INPUT}
              value={form.value}
              placeholder="client.com, *@news.io or a subject word"
              onChange={(e) => setForm({ ...form, value: e.target.value })}
            />
          </label>
          <label className="text-[11px] text-zinc-400">
            Reason
            <input
              aria-label="Rule reason"
              className={INPUT}
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
            />
          </label>
          <div>
            <Btn
              variant="primary"
              disabled={busy || !form.value.trim()}
              onClick={async () => {
                if (
                  await run(() => addGateRule(accessToken, form), 'Rule added')
                ) {
                  setForm(EMPTY_RULE);
                }
              }}
            >
              Add rule
            </Btn>
          </div>
        </div>
      )}
      <section>
        <SectionLabel count={data.rules.length}>Rules</SectionLabel>
        {!data.rules.length && <EmptyNote>No rules yet.</EmptyNote>}
        <div className="flex flex-col gap-1">
          {data.rules.map((r) => (
            <div
              key={r.id}
              className={`flex items-center gap-2 rounded-lg border border-zinc-800 px-2 py-1 text-[12px] ${
                r.is_active ? 'text-zinc-200' : 'text-zinc-500 line-through'
              }`}
            >
              <span className="w-36 shrink-0 text-zinc-400">
                {humanise(r.kind)}
              </span>
              <span className="flex-1 truncate">{r.value}</span>
              <span className="text-[11px] text-zinc-500">
                {r.scope === 'project' ? 'this project' : 'every project'}
              </span>
              {data.can_edit && (
                <Btn
                  variant="ghost"
                  disabled={busy}
                  onClick={() =>
                    run(
                      () => setGateRuleActive(accessToken, r.id, !r.is_active),
                      r.is_active ? 'Rule switched off' : 'Rule switched on',
                    )
                  }
                >
                  {r.is_active ? 'Switch off' : 'Switch on'}
                </Btn>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

// ---- model usage ------------------------------------------------------------

const UsageTable = ({ label, rows, priced }) => (
  <section>
    <SectionLabel count={rows.length}>{label}</SectionLabel>
    {!rows.length && <EmptyNote>No model calls in this period.</EmptyNote>}
    {rows.length > 0 && (
      <div className="overflow-x-auto">
        <table className="w-full text-[12px] text-zinc-300">
          <thead>
            <tr className="text-left text-zinc-400">
              <th className="py-1 font-medium">Name</th>
              <th className="font-medium">Calls</th>
              <th className="font-medium">Failed</th>
              <th className="font-medium">Tokens in / out</th>
              <th className="font-medium">p95</th>
              {priced && <th className="font-medium">Cost (USD)</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map(([name, r]) => (
              <tr key={name}>
                <td className="py-0.5">{name}</td>
                <td>{r.calls}</td>
                <td>
                  {r.failed} ({Math.round(r.failure_rate * 100)}%)
                </td>
                <td>
                  {r.tokens_in} / {r.tokens_out}
                </td>
                <td>
                  {r.p95_ms != null ? `${(r.p95_ms / 1000).toFixed(1)} s` : '-'}
                </td>
                {priced && <td>{r.cost_usd?.toFixed(4)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </section>
);

export const UsageSection = ({ accessToken }) => {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    setData(null);
    getMailUsage(accessToken, days)
      .then((res) => live && (setData(res.data), setError(null)))
      .catch(
        (e) =>
          live &&
          setError(
            isForbidden(e)
              ? 'You need mail settings access to see usage.'
              : errText(e),
          ),
      );
    return () => {
      live = false;
    };
  }, [accessToken, days]);

  if (error) return <ErrorBanner>{error}</ErrorBanner>;
  if (!data) return <Spinner />;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2 text-[13px] text-zinc-400">
        Model calls in the last
        <select
          aria-label="Usage period"
          className="rounded-lg border border-zinc-700 bg-zinc-900 px-2 py-1 text-zinc-100"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
        >
          {[1, 7, 30, 90].map((d) => (
            <option key={d} value={d}>
              {d} day{d > 1 ? 's' : ''}
            </option>
          ))}
        </select>
        {!data.priced && (
          <span className="text-[11px]">
            (cost hidden: set MAIL_INTEL_PRICE_IN_PER_M / _OUT_PER_M)
          </span>
        )}
      </div>
      <UsageTable
        label="By stage"
        rows={Object.entries(data.stages).map(([k, v]) => [humanise(k), v])}
        priced={data.priced}
      />
      <UsageTable
        label="By day"
        rows={Object.entries(data.by_day)}
        priced={data.priced}
      />
      <UsageTable
        label="Top users"
        rows={data.top_owners.map((o) => [o.cost_owner, o])}
        priced={data.priced}
      />
    </div>
  );
};
